import React from 'react';

import { makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { connect } from 'react-redux';

import { ErrorBoundary } from '../../../App';
import { PrivacyTerms } from './partials';

type MapStateToProps = ReturnType<typeof mapStateToProps>;

type InnerProps = MapStateToProps;
type OuterProps = {
  children: React.ReactNode;
  className?: any;
  noLayout?: boolean;
  pageName?: string;
};

type Props = InnerProps & OuterProps;

const useStyles = (configHeaderOffset: number, isHeaderInDom: boolean) => {
  const defaultMobileHeaderOffset = 90;
  const defaultHeaderOffset = 18;
  const mobileOffset =
    configHeaderOffset > 0 ? configHeaderOffset : defaultMobileHeaderOffset;
  const headerOffset =
    configHeaderOffset > 0 ? configHeaderOffset : defaultHeaderOffset;

  return makeStyles((theme: Theme) => ({
    main: {
      display: 'flex',
      flexFlow: 'column nowrap',
      minHeight: '100vh',
      paddingTop: isHeaderInDom ? mobileOffset : 0,

      '& header': {
        padding: theme.spacing(1.25, 2, 1),
      },

      '& header, & article, & section': {
        boxSizing: 'border-box',
        marginBottom: theme.spacing(1.5),
        width: '100%',
      },

      '& footer': {
        marginBottom: theme.spacing(10),
        marginTop: theme.spacing(4),
        padding: theme.spacing(0, 2),
        textAlign: 'left',
      },
    },

    [theme.breakpoints.up('sm')]: {
      main: {
        paddingTop: headerOffset,

        '& header': {
          padding: theme.spacing(2, 3, 1.5),
        },

        '& footer': {
          marginBottom: 0,
          marginLeft: theme.spacing(3),
          marginTop: theme.spacing(3),
          padding: 0,
        },
      },
    },

    [theme.breakpoints.up('md')]: {
      main: {
        margin: theme.spacing(0, 'auto'),
        maxWidth: '960px',
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4),

        '& header': {
          padding: theme.spacing(3.5, 0, 1.5),
        },

        '& header, & article, & section': {
          width: '100%',
        },

        '& footer': {
          marginLeft: 0,
        },
      },
    },

    [theme.breakpoints.up('lg')]: {
      main: {
        display: 'grid',
        gridGap: theme.spacing(2),
        gridTemplateColumns: `1fr 378px`,
        gridTemplateRows: `minmax(76px, min-content) auto 80px`,
        justifyItems: 'start',
        maxWidth: '1195px',
        paddingLeft: 0,
        paddingRight: 0,

        '& header, & article, & section': {
          marginBottom: 0,
          padding: 0,
        },

        '& header': {
          gridColumn: '1',
          gridRow: '1',
          paddingBottom: theme.spacing(1),
          paddingTop: theme.spacing(3.5),
        },
        '& article': {
          gridColumn: '1',
          gridRow: '2',
        },
        '& section': {
          gridColumn: '2',
          gridRow: '1 / span 2',
          paddingTop: theme.spacing(3.5),
          position: 'relative',
        },
        '& footer': {
          gridColumn: '1 / 2',
          gridRow: '3',
        },
      },
    },

    noLayout: {
      display: 'block',
    },
  }))();
};

export function Page(props: Props): JSX.Element {
  const {
    children,
    className = '',
    configHeaderOffset,
    noLayout = false,
    pageName = 'fd-page',
  } = props;

  const isHeaderInDom = document.getElementById('header') !== null;

  const classes = useStyles(configHeaderOffset, isHeaderInDom);

  const pageIdentifierClassName = pageName
    ? `fd-page__${pageName.toLowerCase()}`
    : null;

  const mainStyles = clsx(
    'fd-page',
    pageIdentifierClassName,
    classes.main,
    noLayout && classes.noLayout,
    className || '',
  );

  return (
    <ErrorBoundary identifier={pageName}>
      <main className={mainStyles} id="skip-to-content">
        {children}

        <footer className="fd-footer">
          <PrivacyTerms />
        </footer>
      </main>
    </ErrorBoundary>
  );
}

const mapStateToProps = (state: WebOrder.State) => ({
  configHeaderOffset: state.flipdish.Offset,
});

export default connect(mapStateToProps)(Page);
